import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Phone, Mail, Calendar, StickyNote, CheckSquare, Circle, ArrowRight, AlertTriangle, PartyPopper, type LucideIcon } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'

type Dashboard = {
  period_days: number
  counts: {
    leads_total: number
    leads_new: number
    leads_converted: number
    accounts_total: number
    accounts_customers: number
    contacts_total: number
    opportunities_open: number
    opportunities_won_period: number
    opportunities_lost_period: number
  }
  pipeline_open_total: string
  pipeline_weighted_forecast: string
  won_value_period: string
  lost_value_period: string
  win_rate_period: string
  lead_conversion_rate: string
  pipeline_by_stage: Array<{ stage_id: number; stage_name: string; stage_color: string; count: number; total: string }>
  top_deals: Array<{ id: number; name: string; account_name: string; stage_name: string; amount: string; currency_code: string; probability: string; weighted_amount: string }>
  recent_activities: Array<{ id: number; type: string; subject: string; status: string; owner_username: string | null; created_at: string; related: string | null }>
  next_actions: Array<{ id: number; type: string; subject: string; due_date: string; is_overdue: boolean; related: string | null }>
  activity_by_type: Array<{ type: string; label: string; count: number }>
}

const TYPE_ICON: Record<string, LucideIcon> = {
  CALL: Phone, EMAIL: Mail, MEETING: Calendar, NOTE: StickyNote, TASK: CheckSquare,
}

function TypeIcon({ type, className }: { type: string; className?: string }) {
  const Icon = TYPE_ICON[type] || Circle
  return <Icon className={className || 'h-4 w-4'} />
}

export default function CRMDashboard() {
  const [days, setDays] = useState(30)
  const { data, isLoading } = useQuery({
    queryKey: ['crm', 'dashboard', days],
    queryFn: async () => (await api.get<Dashboard>('/crm/dashboard/', { params: { days } })).data,
  })

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">CRM Dashboard</h2>
          <p className="text-sm text-gray-500">Sales performance over the last {days} days.</p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="px-3 py-1.5 border border-gray-300 rounded text-sm">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {isLoading || !data ? <p className="text-gray-500">Loading…</p> : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KPI label="Open pipeline" value={Number(data.pipeline_open_total).toFixed(2)} sub={`${data.counts.opportunities_open} deals`} />
            <KPI label="Weighted forecast" value={Number(data.pipeline_weighted_forecast).toFixed(2)} highlight />
            <KPI label={`Won (${data.period_days}d)`} value={Number(data.won_value_period).toFixed(2)} sub={`${data.counts.opportunities_won_period} deals`} accent="green" />
            <KPI label={`Win rate (${data.period_days}d)`} value={`${data.win_rate_period}%`} sub={`vs ${data.counts.opportunities_lost_period} lost`} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KPI label="Leads" value={data.counts.leads_total.toString()} sub={`${data.counts.leads_new} new`} small />
            <KPI label="Conversion rate" value={`${data.lead_conversion_rate}%`} sub={`${data.counts.leads_converted} converted`} small />
            <KPI label="Accounts" value={data.counts.accounts_total.toString()} sub={`${data.counts.accounts_customers} customers`} small />
            <KPI label="Contacts" value={data.counts.contacts_total.toString()} small />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Pipeline by stage (bar) */}
            <div className="bg-white rounded-lg shadow p-4 lg:col-span-2">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Pipeline by stage</h3>
              {data.pipeline_by_stage.length === 0 ? (
                <p className="text-sm text-gray-500">No data.</p>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    const maxTotal = Math.max(...data.pipeline_by_stage.map((s) => Number(s.total)), 1)
                    return data.pipeline_by_stage.map((s) => (
                      <div key={s.stage_id} className="flex items-center text-sm">
                        <div className="w-32 text-gray-700 truncate">{s.stage_name}</div>
                        <div className="flex-1 bg-gray-100 rounded h-6 relative overflow-hidden">
                          <div className="absolute inset-y-0 left-0 rounded"
                            style={{
                              width: `${(Number(s.total) / maxTotal) * 100}%`,
                              backgroundColor: s.stage_color || '#94a3b8',
                              opacity: 0.85,
                            }} />
                          <span className="absolute inset-0 flex items-center px-2 text-xs font-medium text-gray-800">
                            {s.count} deal{s.count !== 1 && 's'} · {Number(s.total).toFixed(0)}
                          </span>
                        </div>
                      </div>
                    ))
                  })()}
                </div>
              )}
            </div>

            {/* Activity by type */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Activities ({data.period_days}d)
              </h3>
              <div className="space-y-2">
                {data.activity_by_type.map((a) => (
                  <div key={a.type} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 inline-flex items-center gap-2">
                      <TypeIcon type={a.type} className="h-4 w-4 text-gray-500" />
                      {a.label}
                    </span>
                    <span className="font-mono font-semibold text-gray-900">{a.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top deals */}
            <div className="bg-white rounded-lg shadow p-4 lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Top open deals</h3>
                <Link to="/admin/crm/opportunities" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
              </div>
              {data.top_deals.length === 0 ? (
                <p className="text-sm text-gray-500">No open deals.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {data.top_deals.map((d) => (
                      <tr key={d.id}>
                        <td className="py-2">
                          <div className="font-medium text-gray-800">{d.name}</div>
                          <div className="text-xs text-gray-500">{d.account_name} · {d.stage_name}</div>
                        </td>
                        <td className="py-2 text-right">
                          <div className="font-mono text-gray-900">{d.currency_code} {Number(d.amount).toFixed(2)}</div>
                          <div className="text-xs text-gray-500">@ {Number(d.probability).toFixed(0)}% = {Number(d.weighted_amount).toFixed(2)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* My next actions */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Next actions</h3>
                <Link to="/admin/crm/activities" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
              </div>
              {data.next_actions.length === 0 ? (
                <p className="text-sm text-gray-500 inline-flex items-center gap-1.5">Nothing due in the next 7 days <PartyPopper className="h-4 w-4 text-green-600" /></p>
              ) : (
                <ul className="space-y-2">
                  {data.next_actions.map((a) => (
                    <li key={a.id} className={`text-sm border-l-4 pl-2 ${a.is_overdue ? 'border-red-500' : 'border-blue-400'}`}>
                      <div className="flex items-center">
                        <TypeIcon type={a.type} className="h-4 w-4 mr-2 text-gray-500" />
                        <span className="font-medium text-gray-800 truncate flex-1">{a.subject}</span>
                      </div>
                      <div className="text-xs text-gray-500 ml-6">
                        {a.related && <span>{a.related} · </span>}
                        {new Date(a.due_date).toLocaleString()}
                        {a.is_overdue && (
                          <span className="text-red-700 font-semibold ml-1 inline-flex items-center gap-0.5">
                            <AlertTriangle className="h-3 w-3" /> overdue
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Recent activities */}
            <div className="bg-white rounded-lg shadow p-4 lg:col-span-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Recent activity</h3>
              {data.recent_activities.length === 0 ? (
                <p className="text-sm text-gray-500">No activities yet.</p>
              ) : (
                <ul className="space-y-2">
                  {data.recent_activities.map((a) => (
                    <li key={a.id} className="flex items-center text-sm border-b border-gray-100 pb-2 last:border-0">
                      <TypeIcon type={a.type} className="h-5 w-5 mr-3 text-gray-500" />
                      <div className="flex-1">
                        <span className="font-medium text-gray-800">{a.subject}</span>
                        {a.related && <span className="text-gray-500"> — {a.related}</span>}
                      </div>
                      <span className="text-xs text-gray-400">
                        {a.owner_username && <span>{a.owner_username} · </span>}
                        {new Date(a.created_at).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  )
}

function KPI({
  label, value, sub, highlight, accent, small,
}: {
  label: string
  value: string
  sub?: string
  highlight?: boolean
  accent?: 'green' | 'red'
  small?: boolean
}) {
  const borderClass = highlight ? 'border-l-4 border-blue-500' :
    accent === 'green' ? 'border-l-4 border-green-500' :
    accent === 'red' ? 'border-l-4 border-red-500' : ''
  return (
    <div className={`bg-white rounded-lg shadow p-4 ${borderClass}`}>
      <div className="text-xs uppercase text-gray-500 tracking-wide">{label}</div>
      <div className={`font-mono text-gray-800 mt-1 ${small ? 'text-base' : 'text-2xl'}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  )
}
